/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { PanelType } from '@fluentui/react/lib/Panel';
import { Stack } from '@fluentui/react/lib/Stack';
import { Property } from 'csstype';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { GlobalFeedbackBlade } from 'src/appShell/globalHeader/globalHeaderBlades/GlobalFeedbackBlade';
import { GlobalHelpBlade } from 'src/appShell/globalHeader/globalHeaderBlades/GlobalHelpBlade';
import { GlobalSettingsBlade } from 'src/appShell/globalHeader/globalHeaderBlades/GlobalSettingsBlade';
import { GlobalHeaderBladeType } from 'src/appShell/globalHeader/types';
import { BladeModel } from 'src/core/components/BladeModel';
import { LuisText, LuisTextProps } from 'src/core/components/LuisText';
import { StackBladeHost } from 'src/core/components/StackBladeHost';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylist } from 'src/core/themes/luisStylist';
import { ToastPanel } from 'src/luis/projects/events/components/ToastPanel/ToastPanel';

export type GlobalHeaderBladesProps = {
    /**
     * Determines whether the blade is open or not
     */
    isOpen: boolean;

    /**
     * Determines the type of the blade to render
     */
    bladeType: GlobalHeaderBladeType;

    /**
     * Handler function for blade dismiss
     */
    onLastBladeDismissed: () => void;
};

const messages = defineMessages({
    settingsHeader: {
        id: 'portal.globalHeaderBlades.settingsHeader',
        defaultMessage: 'Settings'
    },
    helpHeader: {
        id: 'portal.globalHeaderBlades.helpHeader',
        defaultMessage: 'Help'
    },
    feedbackHeader: {
        id: 'portal.globalHeaderBlades.feedbackHeader',
        defaultMessage: 'Feedback'
    },
    profileHeader: {
        id: 'portal.globalHeaderBlades.profile',
        defaultMessage: 'Profile'
    },
    notificationsHeader: {
        id: 'portal.globalHeaderBlades.notifications',
        defaultMessage: 'Notifications'
    }
});

const { styleDiv, styleComponent } = getStylist('GlobalHeaderBlades');
const headerTheme = getScopedTheme('GlobalHeaderBlades');
const Container = styleDiv('Container', theme => ({ marginTop: theme.containerMargin }));
const BladeHeader = styleComponent<LuisTextProps>(LuisText)('BladeHeader', theme => ({ height: theme.headerHeight }));

/**
 * Controls and renders the global header blades.
 */
export const GlobalHeaderBlades = observer((props: GlobalHeaderBladesProps) => {
    const { isOpen, onLastBladeDismissed, bladeType } = props;
    const { localize } = useLocalization();

    /**
     * Gets the blade to render.
     */
    const getBlades = (): BladeModel[] => {
        let bladeKey: string;
        let fullWidth = false;
        let bladeHeader: string;
        let bladeContent: React.ReactNode;

        if (bladeType === null) {
            return;
        }

        switch (bladeType) {
            case 'settings':
                fullWidth = true;
                bladeKey = 'settings';
                bladeContent = <GlobalSettingsBlade />;
                break;
            case 'help':
                bladeKey = 'help';
                bladeHeader = localize(messages.helpHeader);
                bladeContent = (
                    <Container>
                        <GlobalHelpBlade />
                    </Container>
                );
                break;
            case 'feedback':
                bladeKey = 'feedback';
                bladeHeader = localize(messages.feedbackHeader);
                bladeContent = (
                    <Container>
                        <GlobalFeedbackBlade />
                    </Container>
                );
                break;
            case 'toasts':
                bladeKey = 'notifications';
                bladeHeader = localize(messages.notificationsHeader);
                bladeContent = (
                    <Container>
                        <Stack verticalAlign="space-evenly" tokens={{ childrenGap: 10 }} grow>
                            <ToastPanel />
                        </Stack>
                    </Container>
                );
                break;
            case 'profile':
                bladeKey = 'profile';
                break;
            default:
                throw new Error('Not a valid blade type');
        }

        return [
            {
                key: bladeKey,
                fullWidth,
                content: bladeContent,
                header: bladeHeader && (
                    <BladeHeader fontWeight={headerTheme.headerFontWeight as Property.FontWeight} variant="xLarge">
                        {bladeHeader}
                    </BladeHeader>
                )
            }
        ];
    };

    const panelType = bladeType === 'settings' ? PanelType.smallFluid : PanelType.custom;

    return (
        <StackBladeHost
            isOpen={isOpen}
            type={panelType}
            blades={getBlades()}
            onDismiss={onLastBladeDismissed}
            topSpacing={headerTheme.topSpacing}
            onLastBladeDismissed={onLastBladeDismissed}
        />
    );
});
